import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { motion } from "framer-motion";

const CONTRACT_ADDRESS = "0x1aFed7a521252C973b130C11E182e57936Cd11a5";
// DOGE_ADDRESS removed – native DOGE is used via provider.getBalance()
const MCRIB_ADDRESS = "0xbdaD927604c5cB78F15b3669a92Fa5A1427d33a2";

const ABI = [
  // PAYABLE mint (so we can send DOGE as msg.value)
  "function mint(uint256 num) external payable",
  // Dynamic pricing / reveal config
  "function dogeMintFee() external view returns (uint256)",
  "function mcribMintPrice() external view returns (uint256)",
  "function revealBlock() external view returns (uint256)",
  "function revealed() external view returns (bool)",
  // Core ERC721-ish stuff
  "function tokenURI(uint256 id) external view returns (string)",
  "function totalSupply() external view returns (uint256)",
  "function ownerOf(uint256 tokenId) external view returns (address)",
  "function balanceOf(address owner) external view returns (uint256)",
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // NEW ERC721Enumerable FUNCTIONS — FOR RANDOM ID TRACKING
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  "function tokenOfOwnerByIndex(address owner, uint256 index) external view returns (uint256)",
  "function tokenByIndex(uint256 index) external view returns (uint256)"
];

const ERC20_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)"
];

const TX_OPTIONS = {
  gasPrice: ethers.utils.parseUnits("250", "gwei"),
  // gasLimit removed → now calculated dynamically inside handleMint
};
const DOGECHAIN = {
  chainId: "0x7D0",
  chainName: "Dogechain",
  rpcUrls: ["https://rpc.dogechain.dog"],
  nativeCurrency: { name: "DOGE", symbol: "DOGE", decimals: 18 },
  blockExplorerUrls: ["https://explorer.dogechain.dog"]
};

// MCRIB_REQUIRED removed – using contract.mcribMintPrice() dynamically
 // 50B × 10¹⁵ (not used now; left in for reference)
// DOGE_REQUIRED removed – using contract.dogeMintFee() dynamically
 // not used now; dynamic from contract

const TRAIT_RARITY = {
  buns: { "Standard": 40, "Toasted": 35, "Extra Crispy": 25 },
  patties: { "Classic": 30, "Smoky": 30, "Double Stack": 25, "Charred": 15 },
  sauces: { "BBQ Drip": 40, "Spicy Glaze": 35, "Sweet Heat": 25 },
  onions: { "None": 20, "Light": 25, "Heavy": 20, "Crispy": 20, "Caramelized": 15 },
  pickles: { "None": 30, "Single": 30, "Double": 25, "Triple": 15 },
  topBuns: { "Sesame": 60, "Plain": 40 },
  bottomBun: {
    "Standard Bottom": 67,
    "Toasted Edge": 20,
    "Buttered Glow": 10,
    "Charred Perfection": 3 // ← 3% CHANCE — LEGENDARY
  }
};

let provider, signer, contract;
if (typeof window !== "undefined" && window.ethereum) {
  provider = new ethers.providers.Web3Provider(window.ethereum);
  signer = provider.getSigner();
  contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, signer);
}

export default function App() {
  const [account, setAccount] = useState("");
  const [recentMints, setRecentMints] = useState([]);
  const [myNFTs, setMyNFTs] = useState([]);
  const [mintedCount, setMintedCount] = useState(0);
  const [cooking, setCooking] = useState(false);
  const [mintStep, setMintStep] = useState("");
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [activeTab, setActiveTab] = useState("recent");
  const [userMcribBalance, setUserMcribBalance] = useState("0");
  const [userDogeBalance, setUserDogeBalance] = useState("0"); // ← NATIVE DOGE (MetaMask's DOGE)

  // ————————————————————————————————————
  // ALL FUNCTIONS — IN CORRECT ORDER TO AVOID "before initialization" ERRORS
  // ————————————————————————————————————
  const switchToDogechain = async () => {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: DOGECHAIN.chainId }]
      });
    } catch (e) {
      if (e.code === 4902) {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [DOGECHAIN]
        });
      }
    }
  };

  const connectWallet = async () => {
    try {
      await switchToDogechain();
      const accounts = await provider.send("eth_requestAccounts", []);
      setAccount(accounts[0]);
    } catch (e) {
      alert("Wallet connection failed: " + e.message);
    }
  };

  const disconnectWallet = () => {
    setAccount("");
    setMyNFTs([]);
    setSelectedNFT(null);
    setUserMcribBalance("0");
    setUserDogeBalance("0");
  };

  // Pulls NATIVE DOGE balance (MetaMask's "DOGE")
  const loadBalances = async () => {
    if (!account) return;
    try {
      const mcrib = new ethers.Contract(MCRIB_ADDRESS, ERC20_ABI, provider);
      const nativeDogeBal = await provider.getBalance(account); // ← NATIVE DOGE
      const mcribBalRaw = await mcrib.balanceOf(account);
      setUserMcribBalance(ethers.utils.formatUnits(mcribBalRaw, 15));
      setUserDogeBalance(ethers.utils.formatEther(nativeDogeBal)); // ← THIS IS THE ONE YOU WANT
    } catch (e) {
      console.error("Balance load failed", e);
    }
  };

    const loadData = async () => {
    try {
      const supply = await contract.totalSupply();
      const count = supply.toNumber();
      setMintedCount(count);

      const recent = [];
      for (let i = 0; i < Math.min(count, 9); i++) {
        try {
          const tokenId = await contract.tokenByIndex(count - 1 - i);
          const uri = await contract.tokenURI(tokenId);

          const metadata = await parseTokenURI(uri, tokenId.toString());  // ← NEW UNIVERSAL PARSER

          recent.push({ id: tokenId.toString(), metadata });
        } catch (err) {
          console.warn(`Failed recent token index ${i}`, err);
        }
      }
      setRecentMints(recent);
    } catch (e) {
      console.error("loadData failed:", e);
    }
  };

  const loadMyMcRibs = async () => {
    if (!account || !contract) return;
    setMyNFTs([]);

    try {
      const balance = await contract.balanceOf(account);
      const balanceNum = balance.toNumber();
      if (balanceNum === 0) return;

      const nfts = [];
      for (let i = 0; i < balanceNum; i++) {
        try {
          const tokenId = await contract.tokenOfOwnerByIndex(account, i);
          const uri = await contract.tokenURI(tokenId);

          const metadata = await parseTokenURI(uri, tokenId.toString());  // ← SAME UNIVERSAL PARSER

          nfts.push({ id: tokenId.toString(), metadata });
        } catch (err) {
          console.error(`Failed your token at index ${i}`, err);
        }
      }
      setMyNFTs(nfts);
    } catch (err) {
      console.error("loadMyMcRibs failed:", err);
    }
  };

  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // UNIVERSAL tokenURI PARSER — WORKS EVERY TIME
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←

// FORCE PRE-REVEAL SCREEN — bypasses all parsing issues
const forcePreRevealImage = (tokenId) => {
  return `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiMwMDAwMDAiLz48dGV4dCB4PSIxNiIgeT0iOSIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSIyLjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNGRjZCMDAiPk5PVyBTRVJWSU5HPC90ZXh0Pjx0ZXh0IHg9IjE2IiB5PSIxNiIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSI1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjRkZGRkZGIiBzdHJva2U9IiNGRjZCMDAiIHN0cm9rZS13aWR0aD0iMC44Ij5NY1JJQiAje3Rva2VuSWR9PC90ZXh0Pjx0ZXh0IHg9IjE2IiB5PSIyNCIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSIxLjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM4ODg4ODgiPlJldmVhbHMgaW4gYmxvY2tzPC90ZXh0Pjwvc3ZnPg==`.replace("{{tokenId}}", tokenId);
};

// ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
// FINAL UNIVERSAL tokenURI PARSER — WORKS WITH RAW JSON (current contract)
// ALSO STILL WORKS IF SOMEONE RETURNS BASE64 (future-proof)
// ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←

const parseTokenURI = (uri, tokenId) => {
  try {
    let jsonString;

    // CASE 1: Standard JSON string (your new contract)
    if (uri.startsWith("{")) {
      jsonString = uri;
    }

    // CASE 2: Base64 data URI (old style)
    else if (uri.startsWith("data:application/json")) {
      const b64 = uri.split("base64,")[1];
      jsonString = atob(b64);
    }

    // Parse cleanly (NO escape/unescape nonsense)
    const json = JSON.parse(jsonString);

    return {
      name: json.name || `McRib #${tokenId}`,
      description: json.description || "Fresh McRib. Ready to eat!",
      image: json.image,
      attributes: json.attributes || []
    };
  } catch (e) {
    console.error("Metadata parse failed for token", tokenId, e);
    console.log("RAW URI:", uri);

    // DO NOT RETURN 32×32 fallback
    return {
      name: `McRib #${tokenId}`,
      description: "Loading…",
      image: null,
      attributes: []
    };
  }
};

const [mintQuantity, setMintQuantity] = useState(1);

const handleMint = async () => {
  if (!account) return alert("Connect wallet first");
  if (mintQuantity < 1 || mintQuantity > 20) {
    return alert("You can only mint 1–20 McRibs at a time");
  }

  setCooking(true);
  setMintStep(`Preparing grill for ${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""}...`);

  try {
    const mcrib = new ethers.Contract(MCRIB_ADDRESS, ERC20_ABI, signer);

    // ============================
    // DEBUG START — BEFORE MINT
    // ============================
    console.log("===== MULTI-MINT DEBUG START =====");
    console.log(`Minting quantity: ${mintQuantity}`);

    const dogeBefore = await provider.getBalance(account);
    const mcribBefore = await mcrib.balanceOf(account);
    console.log("DOGE BEFORE:", ethers.utils.formatEther(dogeBefore));
    console.log("MCRIB BEFORE:", ethers.utils.formatUnits(mcribBefore, 15));

    const dogeFeePer = await contract.dogeMintFee();
    const mcribPricePer = await contract.mcribMintPrice();

    const totalDogeRequired = dogeFeePer.mul(mintQuantity);
    const totalMcribRequired = mcribPricePer.mul(mintQuantity);

    console.log("Per mint — DOGE fee:", ethers.utils.formatEther(dogeFeePer));
    console.log("Per mint — MCRIB price:", ethers.utils.formatUnits(mcribPricePer, 15));
    console.log(`TOTAL REQUIRED → DOGE: ${ethers.utils.formatEther(totalDogeRequired)} | MCRIB: ${ethers.utils.formatUnits(totalMcribRequired, 15)}B`);

    // ============================
    // CHECK REMAINING SUPPLY (NEW!)
    // ============================
    setMintStep("Checking remaining supply...");
    const currentSupply = await contract.totalSupply();
    if (currentSupply.add(mintQuantity).gt(10000)) {
      throw new Error(`Only ${10000 - currentSupply.toNumber()} McRibs left — can't mint ${mintQuantity}`);
    }

    // ============================
    // 1. CHECK NATIVE DOGE BALANCE
    // ============================
    setMintStep("Checking DOGE balance...");
    const nativeBalance = await provider.getBalance(account);
    if (nativeBalance.lt(totalDogeRequired)) {
      throw new Error(
        `Not enough DOGE! Need ${ethers.utils.formatEther(totalDogeRequired)} DOGE (${mintQuantity} × ${ethers.utils.formatEther(dogeFeePer)})`
      );
    }

    // ============================
    // DYNAMIC GAS ESTIMATION (THE FIX!)
    // ============================
    setMintStep("Estimating gas for the cook...");
    let dynamicTxOptions = { ...TX_OPTIONS };

    try {
      const allowanceCheck = await mcrib.allowance(account, CONTRACT_ADDRESS);
      const estimatedApprove = allowanceCheck.lt(totalMcribRequired)
        ? await mcrib.estimateGas.approve(CONTRACT_ADDRESS, totalMcribRequired.mul(10))
        : ethers.BigNumber.from(0);

      const estimatedMint = await contract.estimateGas.mint(mintQuantity, { value: totalDogeRequired });

      const totalGas = estimatedApprove.add(estimatedMint).mul(130).div(100); // +30% buffer
      dynamicTxOptions.gasLimit = totalGas.gt(800000) ? totalGas : ethers.BigNumber.from(800000);

      console.log("DYNAMIC GAS LIMIT CALCULATED:", dynamicTxOptions.gasLimit.toString());
    } catch (e) {
      console.warn("Gas estimation failed → using 2M safe fallback");
      dynamicTxOptions.gasLimit = ethers.BigNumber.from(2000000);
    }

    // ============================
    // 2. APPROVE MCRIB (if needed)
    // ============================
    setMintStep("Checking MCRIB approval...");
    const allowance = await mcrib.allowance(account, CONTRACT_ADDRESS);
    if (allowance.lt(totalMcribRequired)) {
      setMintStep(`Approving ${ethers.utils.formatUnits(totalMcribRequired, 15)}B MCRIB...`);
      const approveTx = await mcrib.approve(
        CONTRACT_ADDRESS,
        totalMcribRequired.mul(10), // generous buffer
        dynamicTxOptions
      );
      await approveTx.wait();
      console.log("MCRIB APPROVED FOR", ethers.utils.formatUnits(totalMcribRequired.mul(10), 15) + "B");
    } else {
      console.log("MCRIB already approved");
    }

    // ============================
    // 3. MINT MULTIPLE McRIBS
    // ============================
    setMintStep(`Grilling ${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""}...`);
    console.log("Sending mint tx with value:", ethers.utils.formatEther(totalDogeRequired), "DOGE");

    const mintTx = await contract.mint(mintQuantity, {
      ...dynamicTxOptions,    // ← NOW USING DYNAMIC GAS
      value: totalDogeRequired
    });

    setMintStep("On the blockchain... almost there!");
    const receipt = await mintTx.wait();
    console.log("MINT TX SUCCESS:", receipt.transactionHash);

    // ============================
    // DEBUG END — AFTER MINT
    // ============================
    const dogeAfter = await provider.getBalance(account);
    const mcribAfter = await mcrib.balanceOf(account);

    console.log("DOGE AFTER:", ethers.utils.formatEther(dogeAfter));
    console.log("MCRIB AFTER:", ethers.utils.formatUnits(mcribAfter, 15));
    console.log("DOGE DEDUCTED:", ethers.utils.formatEther(dogeBefore.sub(dogeAfter)));
    console.log("MCRIB DEDUCTED:", ethers.utils.formatUnits(mcribBefore.sub(mcribAfter), 15));
    console.log("===== MULTI-MINT DEBUG END =====");

    // ============================
    // SUCCESS & REFRESH
    // ============================
    setMintStep("McRibs served fresh! Reloading tray...");
    await loadData();
    await loadBalances();
    await new Promise(r => setTimeout(r, 6000));
    await loadMyMcRibs();

    alert(`${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""} successfully minted!`);

  } catch (error) {
    console.error("Mint failed:", error);

    // Keep your smart fallback error message
    try {
      const fee = await contract.dogeMintFee();
      const price = await contract.mcribMintPrice();
      const totalDoge = fee.mul(mintQuantity);
      const totalMcrib = price.mul(mintQuantity);

      alert(
        `Mint failed — Required for ${mintQuantity}:\n` +
        `${ethers.utils.formatEther(totalDoge)} DOGE + ${ethers.utils.formatUnits(totalMcrib, 15)}B MCRIB\n\n` +
        (error.message || "Check wallet & balances")
      );
    } catch {
      alert("Mint failed — check network, balances, or try again.");
    }
  } finally {
    setTimeout(() => {
      setCooking(false);
      setMintStep("");
    }, 3000);
  }
};

    useEffect(() => {
    if (account) {
      loadBalances();
      loadMyMcRibs();
    }
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  return (
    // YOUR ENTIRE BEAUTIFUL JSX — 100% UNTOUCHED
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating McRib Background */}
      <div className="fixed inset-0 bg-floating-mcrib opacity-10" />
      {/* Fire & Smoke Title Effect */}
      <div className="fixed top-0 left-0 right-0 flex justify-center pointer-events-none z-20">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 4 }}
          className="fire-glow"
        >
          <h1 className="text-8xl font-pixel text-mcrib-fire opacity-60 blur-xl select-none">
            McRIB PIXELS
          </h1>
        </motion.div>
      </div>
      {/* Minting Overlay */}
      {cooking && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center gap-6">
          <motion.h1
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="text-6xl font-pixel text-mcrib-gold"
          >
            COOKING...
          </motion.h1>
          <div className="w-96 h-8 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-mcrib-red via-mcrib-fire to-mcrib-gold"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 3, ease: "linear" }}
            />
          </div>
          <p className="text-xl text-mcrib-gold font-pixel">{mintStep}</p>
        </div>
      )}
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="text-center pt-20 relative z-10"
      >
        <h1 className="text-7xl font-pixel text-mcrib-gold drop-shadow-2xl relative">
          McRIB PIXELS
          <div className="smoke-particles" />
        </h1>
        <p className="text-2xl mt-4 text-mcrib-red font-bold">
          50B $MCRIB + 1 DOGE
        </p>
        {/* Balance Display — Shows Native DOGE (MetaMask's DOGE) */}
        {account && (
          <div className="mt-4 text-lg font-pixel">
            <span className="text-mcrib-gold">
              MCRIB: {parseFloat(userMcribBalance).toFixed(2)}B
            </span>{" "}
            |{" "}
            <span className="text-mcrib-gold">
              DOGE: {parseFloat(userDogeBalance).toFixed(3)}
            </span>
          </div>
        )}
        <div className="flex justify-center gap-6 mt-8">
          {account ? (
            <div className="flex gap-4 items-center">
              <span className="text-green-400 font-bold">
                {account.slice(0, 6)}...{account.slice(-4)}
              </span>
              <button
                onClick={disconnectWallet}
                className="bg-gray-700 px-4 py-2 rounded-full text-sm"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={connectWallet}
              className="bg-mcrib-red px-8 py-4 rounded-full text-xl font-bold hover:scale-110 transition"
            >
              CONNECT WALLET
            </button>
          )}
        </div>

        {/* === NEW MULTI-MINT SECTION === */}
        <div className="flex flex-col items-center gap-8 mt-12">
          <div className="flex items-center gap-8">
            {[1, 5, 10, 20].map((num) => (
              <button
                key={num}
                onClick={() => setMintQuantity(num)}
                disabled={cooking}
                className={`px-8 py-4 rounded-full font-bold text-2xl transition-all shadow-lg ${
                  mintQuantity === num
                    ? "bg-mcrib-gold text-black scale-110 shadow-2xl"
                    : "bg-mcrib-red/80 text-white hover:scale-105"
                } disabled:opacity-50`}
              >
                {num === 20 ? "MAX" : num}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-6">
            <input
              type="number"
              min="1"
              max="20"
              value={mintQuantity}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                if (val >= 1 && val <= 20) setMintQuantity(val);
                else if (val > 20) setMintQuantity(20);
              }}
              className="w-32 px-6 py-4 bg-black/80 border-4 border-mcrib-gold rounded-2xl text-center text-4xl font-pixel text-mcrib-gold focus:outline-none focus:border-mcrib-fire"
              disabled={cooking}
            />
            <span className="text-3xl font-pixel text-mcrib-gold">McRibs</span>
          </div>

          {account && (
            <div className="text-center">
              <p className="text-2xl font-pixel text-gray-400">Total Cost:</p>
              <p className="text-4xl font-pixel text-mcrib-gold mt-2">
                {(mintQuantity * 1).toFixed(2)} DOGE + {(mintQuantity * 50).toFixed(0)}B $MCRIB
              </p>
              <p className="text-sm text-gray-500 mt-3 font-pixel">
                (Exact price pulled from contract)
              </p>
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.96 }}
            onClick={handleMint}
            disabled={cooking || !account}
            className="bg-gradient-to-r from-mcrib-fire to-mcrib-gold text-black px-20 py-8 rounded-full text-5xl font-bold shadow-3xl disabled:opacity-50 disabled:cursor-not-allowed mt-8"
          >
            {cooking ? "COOKING..." : `MINT ${mintQuantity} McRIB${mintQuantity > 1 ? "S" : ""}!`}
          </motion.button>
        </div>
      </motion.header>
                  {/* Tabs — CLEAN, NO COMMENTS, NO BULLSHIT */}
      <div className="flex justify-center gap-12 mt-12 text-3xl font-pixel tracking-wider">
        <button
          onClick={() => setActiveTab("recent")}
          className={activeTab === "recent" ? "text-mcrib-gold drop-shadow-lg" : "text-gray-500"}
        >
          FRESH OFF THE GRILL
        </button>
        <button
          onClick={() => {
            setActiveTab("mynfts");
            loadMyMcRibs();  // quietly reloads only YOUR NFTs when you click
          }}
          className={activeTab === "mynfts" ? "text-mcrib-gold drop-shadow-lg" : "text-gray-500"}
        >
          MY McRIBS ({myNFTs.length})
        </button>
      </div>

      {/* Traits Grid — UNTOUCHED */}
      <div className="max-w-6xl mx-auto mt-16 px-4 grid grid-cols-2 md:grid-cols-3 gap-8">
        {Object.entries(TRAIT_RARITY).map(([trait, options]) => (
          <div
            key={trait}
            className="bg-black/60 backdrop-blur-md border-2 border-mcrib-gold/50 rounded-2xl p-6"
          >
            <h3 className="text-mcrib-gold font-pixel text-lg uppercase mb-4">
              {trait}
            </h3>
            {Object.entries(options).map(([name, rarity]) => (
              <motion.div
                key={name}
                whileHover={{ scale: 1.05, backgroundColor: "#ff450030" }}
                className="bg-black/50 rounded-lg p-3 text-center border border-mcrib-gold/30 mb-2 cursor-pointer"
                title={`${rarity}% rarity (${rarity * 100} of 10,000)`}
              >
                <div className="text-sm">{name}</div>
                <div className="text-xs text-mcrib-gold">
                  {rarity}% rarity
                </div>
              </motion.div>
            ))}
          </div>
        ))}
      </div>

      {/* PERSONAL TRAY — ONLY CONNECTED WALLET'S McRIBS */}
      <div className="max-w-7xl mx-auto mt-20 px-4">
        {activeTab === "mynfts" && myNFTs.length === 0 ? (
          <div className="text-center py-32">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="inline-block"
            >
              <div className="bg-black/80 backdrop-blur-xl border-4 border-dashed border-mcrib-gold/60 rounded-3xl px-16 py-24 shadow-2xl">
                <h2 className="text-7xl font-pixel text-mcrib-gold mb-6 tracking-wider opacity-90">
                  YOUR TRAY IS EMPTY
                </h2>
                <p className="text-3xl text-mcrib-red/90 font-pixel">
                  Mint your first McRib to fill it up
                </p>
                <div className="mt-10 text-mcrib-gold/50 font-pixel text-2xl">
                  The grill is hot. The sauce is dripping.
                </div>
              </div>
            </motion.div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-10 justify-center">
            {myNFTs.map(({ id, metadata }) => (
              <motion.div
                key={id}
                whileHover={{ y: -50, scale: 1.35 }}
                transition={{ type: "spring", stiffness: 300, damping: 12 }}
                onClick={() => setSelectedNFT({ id, metadata })}
                className="relative group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-2xl border-8 border-mcrib-gold shadow-2xl aspect-square w-72 h-72 mx-auto">
                  {/* Lid */}
                  <div className="absolute top-0 left-0 right-0 h-32 bg-mcrib-red/95 border-t-8 border-x-8 border-mcrib-gold rounded-t-2xl z-20 transition-transform duration-600 group-hover:-translate-y-48" />

                  {/* McRib — FINAL BULLETPROOF RENDERER */}
                  <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                    {metadata.image && metadata.image.startsWith("data:image/svg+xml;base64,") ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: atob(metadata.image.replace("data:image/svg+xml;base64,", ""))
                        }}
                        className="w-full h-full scale-110 group-hover:scale-160 group-hover:translate-y-20 transition-all duration-700"
                        style={{ pointerEvents: "none" }}
                      />
                    ) : (
                      <img
                        src={metadata.image}
                        alt={`McRib #${id}`}
                        className="w-full h-full object-cover scale-110 group-hover:scale-160 group-hover:translate-y-20 transition-all duration-700"
                      />
                    )}
                  </div>

                  {/* Label */}
                  <div className="absolute bottom-6 left-0 right-0 text-center z-30">
                    <p className="font-pixel text-mcrib-gold text-3xl drop-shadow-2xl bg-black/80 px-8 py-3 rounded-full inline-block">
                      McRIB #{id}
                    </p>
                  </div>

                  {/* Fire glow */}
                  <div className="absolute inset-0 bg-mcrib-fire/40 blur-3xl scale-150 group-hover:scale-200 transition-all duration-500 -z-10" />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* NFT MODAL — TALL BADDIE EDITION */}
      {selectedNFT && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6 md:p-12 overflow-y-auto"
          onClick={() => setSelectedNFT(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative bg-black/90 backdrop-blur-xl rounded-3xl border-4 border-mcrib-gold shadow-3xl max-w-6xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button — Top Right */}
            <button
              onClick={() => setSelectedNFT(null)}
              className="absolute top-4 right-4 bg-mcrib-red/90 hover:bg-mcrib-red text-white px-6 py-3 rounded-full font-bold text-lg z-10 shadow-lg hover:scale-110 transition"
            >
              CLOSE ×
            </button>

            <div className="flex flex-col lg:flex-row gap-8 p-8 lg:p-12">
              {/* LEFT: FULL HEIGHT McRIB IMAGE */}
              <div className="flex-1 flex items-center justify-center min-h-96 lg:min-h-full">
                <div className="relative w-full max-w-md">
                  {selectedNFT.metadata.image && selectedNFT.metadata.image.startsWith("data:image/svg+xml;base64,") ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: atob(selectedNFT.metadata.image.replace("data:image/svg+xml;base64,", ""))
                      }}
                      className="w-full h-auto rounded-3xl shadow-2xl overflow-hidden"
                      style={{ maxHeight: '80vh' }}
                    />
                  ) : (
                    <img
                      src={selectedNFT.metadata.image}
                      alt={`McRib #${selectedNFT.id}`}
                      className="w-full h-auto rounded-3xl shadow-2xl"
                      style={{ maxHeight: '80vh', objectFit: 'contain' }}
                    />
                  )}
                </div>
              </div>

              {/* RIGHT: TITLE + TRAITS (2 COLUMNS) */}
              <div className="flex-1 flex flex-col justify-center space-y-6">
                <div className="text-center lg:text-left">
                  <h2 className="text-5xl md:text-6xl font-pixel text-mcrib-gold drop-shadow-2xl">
                    McRib #{selectedNFT.id}
                  </h2>
                  <p className="text-xl text-mcrib-red/90 mt-2 font-bold">Fresh off the grill</p>
                </div>

                {/* Traits Grid — 2 Columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  {selectedNFT.metadata.attributes?.map((attr) => (
                    <div
                      key={attr.trait_type}
                      className="bg-gradient-to-br from-mcrib-gold/20 to-mcrib-fire/10 border-2 border-mcrib-gold/50 rounded-xl p-5 text-center backdrop-blur-sm"
                    >
                      <div className="text-sm text-gray-400 font-pixel uppercase tracking-wider">
                        {attr.trait_type}
                      </div>
                      <div className="text-xl font-bold text-mcrib-gold mt-1">
                        {attr.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="text-center mt-20 pb-20">
        <p className="text-3xl font-pixel text-mcrib-gold">
          {mintedCount} / 10,000 MINTED
        </p>
      </div>
    </div>
  );
}